"use client";

import { c as _c } from "react/compiler-runtime";
import OTPInput from "@/components/OTPInput";
import UniversalButton from "@/components/common/UniversalButton";
import React, { useState, useEffect, Suspense } from "react";
import { useRouter } from "next/navigation";
import OwnerDetailsForm from "./OwnerDetailsForm";
import BusinessDetailsForm from "./BusinessDetailsForm";
import { ChevronLeft, ChevronRight } from "react-feather";
import TermsText from "./BusinessDetailsForm/components/TermsText";
import tooffiLogo from "@/assets/images/tooffi-logo.svg";
import ImageComponent from "@/components/ui/image";
import { registerVendor } from "@/services/api/vendor/register";
import PartnerWithUsSkeleton from "./PartnerWithUsSkeleton";
interface FormData {
  businessName: string;
  location: string;
  pincode: string;
  fssaiNumber?: string | null;
  gstNumber?: string;
  ownerName: string;
  ownerContactNumber: string;
  email: string;
  otp: string;
  latitude: string;
  longitude: string;
}
interface VendorProfile {
  id?: number;
  vendor_email_id?: string;
  vendor_contact_number?: string;
  business_name?: string;
  gst_number?: string;
  vendor_contact_name?: string;
  [key: string]: unknown;
}
const breadcrumbSteps = [{
  id: 1,
  title: "Business Details",
  description: "Enter business information"
}, {
  id: 2,
  title: "Owner Details",
  description: "Enter owner information"
}, {
  id: 3,
  title: "Verification",
  description: "Verify your account"
}];
const PartnerWithUsBody = ({
  searchParams
}: {
  searchParams: Record<string, string | string[] | undefined>;
}) => {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({});
  const [otp, setOTP] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [hasResent, setHasResent] = useState(false);

  // Convert searchParams object to URLSearchParams for easier use
  const params = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, Array.isArray(value) ? value[0] : value);
    }
  });

  // Update URL when step changes
  const updateUrlStep = (newStep: number) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set("step", newStep.toString());
    router.push(`?${newParams.toString()}`, {
      scroll: false
    });
  };

  // Handle step change with URL update
  const handleStepChange = (newStep_0: number) => {
    setStep(newStep_0);
    updateUrlStep(newStep_0);
  };

  // Resend timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);
  const startResendTimer = () => {
    const initialDelay = hasResent ? 60 : 30;
    setResendTimer(initialDelay);
  };
  const handleResendOTP = async () => {
    if (resendTimer > 0 || isLoading) return;
    setOTP("");
    setIsLoading(true);
    setErrorMessage("");
    try {
      const otpResponse = await fetch("/api/generate-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          phone_number: formData.ownerContactNumber
        })
      });
      const otpResult = await otpResponse.json();
      if (otpResponse.ok) {
        setHasResent(true);
        startResendTimer();
        setOTP("");
      } else {
        setErrorMessage(otpResult.error || "Failed to resend OTP");
      }
    } catch (error) {
      setErrorMessage("Failed to resend OTP. Please try again.");
    }
    setIsLoading(false);
  };

  // Initialize step from props
  useEffect(() => {
    const stepParam = params.get("step");
    if (stepParam) {
      const urlStep = parseInt(stepParam);
      if (urlStep >= 1 && urlStep <= 4) {
        setStep(urlStep);
      }
    }
  }, [searchParams, params]);
  const handleBack = () => {
    if (step > 1 && step <= 3) {
      if (step === 3) setOTP("");
      handleStepChange(step - 1);
    }
  };
  const handleBreadcrumbClick = (targetStep: number) => {
    if (targetStep < step && targetStep >= 1) {
      handleStepChange(targetStep);
    }
  };
  const checkExistingVendor = async (formData_0: FormData): Promise<string | null> => {
    try {
      const response = await fetch("/api/vendor_profiles", {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      const result = await response.json();
      if (!response.ok) return "Failed to validate vendor details. Please try again.";
      const vendorProfiles: VendorProfile[] = result.data?.results || result.data || [];
      if (!Array.isArray(vendorProfiles)) return "Failed to validate vendor details. Please try again.";
      const duplicates: string[] = [];
      vendorProfiles.forEach((vendor: VendorProfile) => {
        if (vendor.email && vendor.email.toLowerCase() === formData_0.email?.toLowerCase()) {
          duplicates.push(`Email "${formData_0.email}" is already registered`);
        }
        if (vendor.mobile_number && vendor.mobile_number === formData_0.ownerContactNumber) {
          duplicates.push(`Phone number "${formData_0.ownerContactNumber}" is already registered`);
        }
        if (vendor.business_name && vendor.business_name.toLowerCase() === formData_0.businessName?.toLowerCase()) {
          duplicates.push(`Business name "${formData_0.businessName}" is already registered`);
        }
        if (vendor.gst_number && vendor.gst_number === formData_0.gstNumber) {
          duplicates.push(`GST number "${formData_0.gstNumber}" is already registered`);
        }
      });
      return duplicates.length > 0 ? duplicates[0] : null;
    } catch (error_0) {
      return "Failed to validate vendor details. Please try again.";
    }
  };
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setErrorMessage("");
    if (step === 1) {
      const updatedFormData = {
        ...formData,
        ...data
      };
      setFormData(updatedFormData);
      localStorage.setItem("partnerFormData", JSON.stringify(updatedFormData));
      handleStepChange(2);
      setIsLoading(false);
    } else if (step === 2) {
      const updatedFormData_0 = {
        ...formData,
        ...data
      };
      setFormData(updatedFormData_0);
      localStorage.setItem("partnerFormData", JSON.stringify(updatedFormData_0));
      try {
        const duplicateError = await checkExistingVendor(updatedFormData_0);
        if (duplicateError) {
          setErrorMessage(duplicateError);
          setIsLoading(false);
          return;
        }
        const otpResponse_0 = await fetch("/api/generate-otp", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            phone_number: updatedFormData_0.ownerContactNumber
          })
        });
        const otpResult_0 = await otpResponse_0.json();
        if (otpResponse_0.ok) {
          handleStepChange(3);
          startResendTimer();
        } else {
          setErrorMessage(otpResult_0.error || "Failed to send OTP");
        }
      } catch (error_1) {
        setErrorMessage("Failed to validate details. Please try again.");
      }
      setIsLoading(false);
    }
  };
  const handleOTPSubmit = async (otpValue?: string) => {
    setIsLoading(true);
    setErrorMessage("");
    const otpString = typeof otpValue === "string" ? otpValue : otp;
    if (otpString.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP");
      setIsLoading(false);
      return;
    }
    try {
      const verifyResponse = await fetch("/api/verify-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          phone_number: formData.ownerContactNumber,
          otp: otpString
        })
      });
      const verifyResult = await verifyResponse.json();
      if (!verifyResponse.ok) {
        setOTP("");
        setErrorMessage(verifyResult.error || "Invalid OTP");
        setIsLoading(false);
        return;
      }
      const businessLocation = localStorage.getItem("business_location");
      let latitude: string | null = null;
      let longitude: string | null = null;
      if (businessLocation) {
        try {
          const parsedLocation = JSON.parse(businessLocation);
          latitude = parsedLocation.lat?.toString() || null;
          longitude = parsedLocation.lng?.toString() || null;
        } catch (error_3) {
          console.error("Error parsing user_location from localStorage:", error_3);
        }
      }
      if (!latitude || !longitude) {
        setErrorMessage("Failed to fetch latitude and longitude. Please select a valid location.");
        setIsLoading(false);
        return;
      }
      const vendorPayload = {
        mobile_number: formData.ownerContactNumber,
        email: formData.email,
        business_name: formData.businessName,
        area_name: formData.location,
        pin_code: formData.pincode,
        fssai_number: formData.fssaiNumber || undefined,
        gst_number: formData.gstNumber,
        vendor_contact_name: formData.ownerName,
        latitude: latitude ? parseFloat(latitude) : 0,
        longitude: longitude ? parseFloat(longitude) : 0
      };
      try {
        await registerVendor(vendorPayload);
      } catch (error_4) {
        setOTP("");
        setErrorMessage("Failed to register vendor");
        setIsLoading(false);
        return;
      }
      localStorage.removeItem("partnerFormData");
      handleStepChange(4);
      setFormData({});
      setOTP("");
    } catch (error_2) {
      setOTP("");
      setErrorMessage("Registration failed. Please try again.");
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (step < 4) {
      const savedData = localStorage.getItem("partnerFormData");
      if (savedData) {
        try {
          setFormData(JSON.parse(savedData));
        } catch (error_5) {
          localStorage.removeItem("partnerFormData");
        }
      }
    } else {
      setFormData({});
      setOTP("");
    }
  }, [step]);
  return <div className={`flex flex-col items-center justify-center space-y-4 w-full ${step === 4 ? "max-sm:mt-32" : "max-sm:mt-0"}`}>
      {/* Back Button */}
      {step > 1 && step <= 3 && <div className="w-full flex justify-start">
          <button type="button" onClick={handleBack} className="text-gray-700 py-2 px-4 rounded-lg flex items-center font-raleway font-semibold hover:bg-gray-100 transition-colors" disabled={isLoading}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        </div>}

      {/* Breadcrumbs */}
      {step <= 3 && <div className="w-full max-w-xl">
          <nav className="flex items-center justify-between mb-4" aria-label="Breadcrumb">
            {breadcrumbSteps.map((breadcrumbStep, index) => <div key={breadcrumbStep.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <button onClick={() => handleBreadcrumbClick(breadcrumbStep.id)} disabled={breadcrumbStep.id >= step || isLoading} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${breadcrumbStep.id === step ? "bg-[#FD5C63] text-white" : breadcrumbStep.id < step ? "bg-[#079EA6] text-white hover:bg-[#067d83] cursor-pointer" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
                    {breadcrumbStep.id}
                  </button>
                  <span className={`text-xs mt-1 text-center font-medium ${breadcrumbStep.id === step ? "text-[#FD5C63]" : breadcrumbStep.id < step ? "text-[#079EA6]" : "text-gray-500"}`}>
                    {breadcrumbStep.title}
                  </span>
                </div>
                {index < breadcrumbSteps.length - 1 && <ChevronRight className={`w-4 h-4 mx-2 ${breadcrumbStep.id < step ? "text-[#079EA6]" : "text-gray-300"}`} />}
              </div>)}
          </nav>
        </div>}

      {errorMessage && <p className="text-red-500 text-sm text-center">{errorMessage}</p>}

      {step < 4 && <div className="flex justify-between space-x-3 lg:w-[500px] max-w-xl w-full pb-8">
          {[1, 2, 3].map(s => <div key={s} className={`w-1/3 h-1 ${step >= s ? "bg-[#FD5C63]" : "bg-gray-300"} rounded-lg`}></div>)}
        </div>}

      {step === 1 && <BusinessDetailsForm onSubmit={data_0 => onSubmit({
      ...formData,
      ...data_0
    } as FormData)} initialData={{
      businessName: formData.businessName || "",
      location: formData.location || "",
      pincode: formData.pincode || "",
      fssaiNumber: formData.fssaiNumber || "",
      gstNumber: formData.gstNumber || ""
    }} isLoading={isLoading} />}

      {step === 2 && <OwnerDetailsForm onSubmit={ownerData => onSubmit({
      ...formData,
      ...ownerData
    } as FormData)} initialData={{
      ownerName: formData.ownerName || "",
      ownerContactNumber: formData.ownerContactNumber || "",
      email: formData.email || ""
    }} isLoading={isLoading} />}

      {step === 3 && <div className="space-y-4 w-full">
          <h2 className="text-center text-[#1E0C42] font-semibold text-2xl">
            Verify Account
          </h2>
          <p className="text-center text-gray-600 text-sm">
            Please enter the 6-digit OTP sent to {formData.ownerContactNumber}
          </p>
          <OTPInput length={6} value={otp} error={errorMessage} onComplete={handleOTPSubmit} onChange={setOTP} />
          <div className="flex justify-center">
            <button type="button" onClick={handleResendOTP} disabled={resendTimer > 0 || isLoading} className={`text-sm font-medium ${resendTimer > 0 || isLoading ? "text-gray-400 cursor-not-allowed" : "text-[#079EA6] hover:text-[#067d83] cursor-pointer"}`}>
              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
            </button>
          </div>
          <div className="flex flex-col items-center justify-center space-y-3 pt-14">
            <UniversalButton text={isLoading ? "Verifying & Registering..." : "Submit OTP"} onClick={handleOTPSubmit} variant="custom" classNames="w-2/3 bg-[#FD5C63] text-white py-2 rounded-lg" disabled={isLoading} />
            <TermsText />
          </div>
        </div>}

      {step === 4 && <div className="text-center mt-24 space-y-4 flex flex-col items-center w-full">
          <h2 className="text-[#1E0C42] font-bold lg:text-3xl text-lg font-raleway">
            Request Submitted Successfully!
          </h2>
          <p className="text-[#1E0C42] font-light text-sm pt-4 max-w-md mx-auto">
            Thanks for trusting us! We're reviewing your request and will reach
            out within 24 hours to guide you through the next steps.
          </p>
          <div className="pt-8 w-full flex justify-center">
            <UniversalButton text="Explore Tooffi" onClick={() => router.push("/")} variant="custom" classNames="w-2/3 max-w-xs bg-[#079EA6] text-white py-3 rounded-lg font-semibold hover:bg-[#067d83] transition-colors shadow-sm" />
          </div>
        </div>}
    </div>;
};
const PartnerWithUsClient = t0 => {
  const $ = _c(5);
  const {
    searchParams
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ImageComponent alt="tooffi" src={tooffiLogo} className="w-24 lg:w-[152px] aspect-[124/47]" width={128} height={48} style={{
      height: "auto"
    }} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="lg:text-3xl text-xl font-bold text-center font-raleway text-[#1E0C42]">Partner <span className="text-[#079EA6]">With Us</span></h2>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PartnerWithUsSkeleton />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== searchParams) {
    t4 = <div className="min-h-screen flex flex-col items-center justify-start bg-white"><div className="w-full max-w-2xl bg-white p-6"><div className="flex flex-col items-center justify-center space-y-4">{t1}{t2}<Suspense fallback={t3}><PartnerWithUsBody searchParams={searchParams} /></Suspense></div></div></div>;
    $[3] = searchParams;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
export default PartnerWithUsClient;